import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Typography,
  Tooltip,
  IconButton,
  Alert,
  Skeleton
} from '@mui/material';
import { Info as InfoIcon, Save as SaveIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';

const QuotaManagementForm = ({ quotaData, onSave }) => {
  const { control, handleSubmit, formState: { errors, isDirty }, reset } = useForm({
    defaultValues: quotaData.reduce((acc, quota) => {
      acc[quota.name] = quota.value;
      return acc;
    }, {})
  });

  const handleFormSave = (formData) => {
    // Transform form data back to quota format for the API
    const updatedQuotas = quotaData.map(quota => ({
      ...quota,
      value: parseInt(formData[quota.name], 10)
    }));

    // Call the parent's handleSave function
    onSave(updatedQuotas);

    // Reset form dirty state after successful save
    reset(formData);
  };



  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Quota Management
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage case quotas and limits. Hover over the info icon to see descriptions.
      </Typography>

      <form onSubmit={handleSubmit(handleFormSave)}>
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 250 }}>
                  Quota Name
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 140 }}>
                  Default Quota
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 140 }}>
                  Current Usage
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 140 }}>
                  Current Quota
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 140 }}>
                  Quota Unit
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {quotaData.map((quota, index) => (
                <TableRow
                  key={quota.name}
                  sx={{
                    '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
                    '&:hover': { backgroundColor: 'action.selected' }
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {quota.name}
                      </Typography>
                      <Tooltip title={quota.description} arrow placement="top">
                        <IconButton size="small" sx={{ color: 'primary.main' }}>
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontStyle: 'italic',
                        color: 'text.secondary',
                        fontWeight: 500
                      }}
                    >
                      {quota.defaultValue}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontStyle: 'italic',
                        color: 'text.secondary',
                        fontWeight: 500
                      }}
                    >
                      {quota.usage || 'N/A'}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Controller
                      name={quota.name}
                      control={control}
                      rules={{
                        required: 'Value is required',
                        min: {
                          value: quota.defaultValue,
                          message: `Value cannot be below default (${quota.defaultValue})`
                        },
                        max: {
                          value: 999999,
                          message: 'Value cannot exceed 999,999'
                        },
                        validate: {
                          isInteger: (value) => {
                            const num = parseInt(value, 10);
                            return Number.isInteger(num) || 'Must be a valid integer';
                          }
                        }
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          size="small"
                          variant="outlined"
                          type="number"
                          error={!!errors[quota.name]}
                          helperText={errors[quota.name]?.message}
                          sx={{ width: 120 }}
                          inputProps={{
                            min: quota.defaultValue,
                            max: 999999,
                            step: 1
                          }}
                          onChange={(e) => {
                            // Ensure we're working with integers
                            const value = e.target.value === '' ? '' : parseInt(e.target.value, 10);
                            field.onChange(value);
                          }}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontStyle: 'italic',
                        color: 'text.secondary',
                        fontWeight: 500
                      }}
                    >
                      {quota.unit}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            {isDirty && (
              <Alert severity="info" icon={false}>
                You have unsaved changes
              </Alert>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => reset()}
              disabled={!isDirty}
            >
              Reset Changes
            </Button>

            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!isDirty || Object.keys(errors).length > 0}
              sx={{ minWidth: 140 }}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

// Usage example:
// const handleSave = (updatedQuotas) => {
//   console.log('Saving quotas:', updatedQuotas);
//   // Your API call implementation here
//   // e.g., api.updateQuotas(updatedQuotas);
// };
//
// <QuotaManagementForm
//   quotaData={yourQuotaData}
//   onSave={handleSave}
// />

const QuotaManagementSkeleton = () => {
  // Create array for skeleton rows (typically 3-5 rows to match common quota counts)
  const skeletonRows = Array.from({ length: 4 }, (_, index) => index);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Quota Management
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage case quotas and limits. Hover over the info icon to see descriptions.
      </Typography>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 250 }}>
                Quota Name
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 140 }}>
                Default Quota
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 140 }}>
                Current Usage
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 140 }}>
                Current Quota
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 140 }}>
                Quota Unit
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {skeletonRows.map((index) => (
              <TableRow
                key={index}
                sx={{
                  '&:nth-of-type(odd)': { backgroundColor: 'action.hover' }
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Skeleton variant="text" width={180} height={24} />
                    <Skeleton variant="circular" width={24} height={24} />
                  </Box>
                </TableCell>

                <TableCell>
                  <Skeleton variant="text" width={60} height={20} />
                </TableCell>

                <TableCell>
                  <Skeleton variant="text" width={60} height={20} />
                </TableCell>

                <TableCell>
                  <Box>
                    <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 1 }} />
                  </Box>
                </TableCell>

                <TableCell>
                  <Skeleton variant="text" width={60} height={20} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          {/* Empty space where alert would be */}
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={140} height={36} sx={{ borderRadius: 1 }} />
        </Box>
      </Box>
    </Box>
  );
};

export { QuotaManagementForm, QuotaManagementSkeleton };